import React, { useRef, useState } from "react";
import {
  Box, Button, Card, Checkbox, Divider, FormControlLabel, IconButton,
  ListItemText, Menu, MenuItem, Stack, Typography,
} from "@mui/material";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import StepUpSpinner from "../../../../Components/Loader/StepUpSpinner";

type PdfKind = "detailed" | "compact";

type Props = {
  refreshedAt: Date | null;
  loading: boolean;
  exporting: null | "csv" | "pdf";
  onRefresh: () => void;
  onCsv: () => void;
  onPdf: (kind: PdfKind, opts: { includeIds: boolean }) => void;
};

export default function PremiumHeader({
  refreshedAt, loading, exporting, onRefresh, onCsv, onPdf,
}: Props) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [includeIds, setIncludeIds] = useState(false);
  const anchorRef = useRef<HTMLButtonElement | null>(null);

  const closeMenu = () => setMenuOpen(false);

  return (
    <Card variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
      <Stack direction={{ xs: "column", md: "row" }} alignItems={{ md: "center" }} spacing={2}>
        <Stack direction="row" spacing={2} flex={1} alignItems="center">
          <Box sx={{
            width: 48, height: 48, borderRadius: "50%",
            background: "#eff6ff", color: "#1b3a6b",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <BadgeOutlinedIcon />
          </Box>
          <Box>
            <Typography variant="h6" fontWeight={700} color="#1b3a6b">
              Staff Timetable Report
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Weekly staff timetable — one staff sheet at a time, with printable admin verification.
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {refreshedAt ? `Last refreshed ${refreshedAt.toLocaleTimeString()}` : "—"}
            </Typography>
          </Box>
        </Stack>

        <Stack direction="row" spacing={1} flexWrap="wrap">
          <IconButton onClick={onRefresh} disabled={loading} title="Refresh" size="small"
            sx={{ border: "1px solid #e2e8f0" }}>
            <RefreshOutlinedIcon fontSize="small" />
          </IconButton>
          <Button variant="outlined" size="small"
            startIcon={exporting === "csv" ? <StepUpSpinner size="small" /> : <FileDownloadOutlinedIcon />}
            onClick={onCsv} disabled={loading || !!exporting}>
            {exporting === "csv" ? "Preparing CSV…" : "CSV"}
          </Button>

          <Button
            ref={anchorRef}
            variant="outlined"
            size="small"
            startIcon={exporting === "pdf" ? <StepUpSpinner size="small" /> : <PictureAsPdfOutlinedIcon />}
            endIcon={<ArrowDropDownIcon />}
            onClick={() => setMenuOpen(true)}
            disabled={loading || !!exporting}
          >
            {exporting === "pdf" ? "Preparing PDF…" : "PDF"}
          </Button>
          <Menu
            anchorEl={anchorRef.current}
            open={menuOpen}
            onClose={closeMenu}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            slotProps={{ paper: { sx: { minWidth: 260 } } }}
          >
            <MenuItem
              onClick={() => { closeMenu(); onPdf("detailed", { includeIds: false }); }}
            >
              <ListItemText
                primary="Detailed Staff PDF"
                secondary="One printable sheet per staff, with summary + admin review"
                secondaryTypographyProps={{ variant: "caption" }}
              />
            </MenuItem>
            <MenuItem
              onClick={() => { closeMenu(); onPdf("compact", { includeIds }); }}
            >
              <ListItemText
                primary="All Staff Compact PDF"
                secondary="One combined document, single header, no summary cards"
                secondaryTypographyProps={{ variant: "caption" }}
              />
            </MenuItem>
            <Divider />
            <Box sx={{ px: 2, py: 0.5 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={includeIds}
                    onChange={(e) => setIncludeIds(e.target.checked)}
                    onClick={(e) => e.stopPropagation()}
                  />
                }
                label={<Typography variant="body2">Include Class / Occurrence IDs</Typography>}
                onClick={(e) => e.stopPropagation()}
              />
              <Typography variant="caption" color="text.secondary" display="block">
                Applies to the compact PDF.
              </Typography>
            </Box>
          </Menu>

          <Button variant="outlined" size="small"
            startIcon={<PrintOutlinedIcon />}
            onClick={() => window.print()} disabled={loading}>
            Print
          </Button>
        </Stack>
      </Stack>
    </Card>
  );
}
